import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UserRole } from '../../auth/auth.service';
import { Capability, can } from './permission-matrix';
import { config } from '../../config';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  constructor(private http: HttpClient) {}

  // Every capability check is a thin wrapper over the matrix in
  // ./permission-matrix, which mirrors ab_server/src/services/permissionMatrix.ts
  // entry for entry. These were eleven hand-written `role === X` expressions that
  // had drifted from the server in BOTH directions: PLATFORM_OWNER was excluded
  // from six capabilities the server grants it, and ADMIN was granted
  // canManageChannels, which the server refuses.

  canManageSystem(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_SYSTEM);
  }

  canManageTemplates(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_TEMPLATES);
  }

  canAccessTemplates(role?: UserRole): boolean {
    // Was an unconditional `true`, which also granted an absent or
    // uninterpretable role.
    return can(role, Capability.VIEW_TEMPLATES);
  }

  canManageStages(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_STAGES);
  }

  canManageContacts(role?: UserRole): boolean {
    // Was an unconditional `true`, as above.
    return can(role, Capability.MANAGE_CONTACTS);
  }

  canManageUsers(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_USERS);
  }

  canAccessAdminRoutes(role?: UserRole): boolean {
    return can(role, Capability.ACCESS_ADMIN_ROUTES);
  }

  canManageCompanies(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_COMPANIES);
  }

  canManageChannels(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_CHANNELS);
  }

  canViewChannels(role?: UserRole): boolean {
    return can(role, Capability.VIEW_CHANNELS);
  }

  canManageChannelStatus(role?: UserRole): boolean {
    return can(role, Capability.MANAGE_CHANNEL_STATUS);
  }

  // Get role hierarchy level (higher number = more permissions)
  getRoleLevel(role?: UserRole): number {
    switch (role) {
      case UserRole.PLATFORM_OWNER:
        return 4;
      case UserRole.OWNER:
        return 3;
      case UserRole.ADMIN:
        return 2;
      case UserRole.MEMBER:
        return 1;
      default:
        return 0;
    }
  }

  // Check if user has higher or equal role level
  hasMinimumRole(userRole?: UserRole, requiredRole?: UserRole): boolean {
    return this.getRoleLevel(userRole) >= this.getRoleLevel(requiredRole);
  }

  // Get user-friendly role display name
  getRoleDisplayName(role?: UserRole): string {
    switch (role) {
      case UserRole.PLATFORM_OWNER:
        return 'God';
      case UserRole.OWNER:
        return 'Super Admin';
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.MEMBER:
        return 'User';
      default:
        return 'Unknown';
    }
  }

  // Get role badge class for UI styling
  getRoleBadgeClass(role?: UserRole): string {
    switch (role) {
      case UserRole.PLATFORM_OWNER:
        return 'badge-god';
      case UserRole.OWNER:
        return 'badge-super-admin';
      case UserRole.ADMIN:
        return 'badge-admin';
      case UserRole.MEMBER:
        return 'badge-user';
      default:
        return 'badge-default';
    }
  }

  // NEW: RC-MC hierarchy permission checks
  canRCAccessMC(userRole: UserRole, userCompanyId: string, targetCompanyId: string): Observable<boolean> {
    if (userRole !== UserRole.OWNER) {
      return of(false);
    }
    
    return this.http.post<{canAccess: boolean}>(`${config.apiUrl}/permissions/rc-mc-access`, {
      rcCompanyId: userCompanyId,
      mcCompanyId: targetCompanyId
    }).pipe(
      map(response => response.canAccess),
      catchError(() => of(false))
    );
  }

  // NEW: Company boundary enforcement
  canAccessCompanyData(userRole: UserRole, userCompanyId: string, targetCompanyId: string): Observable<boolean> {
    // God can access everything
    if (userRole === UserRole.PLATFORM_OWNER) {
      return of(true);
    }
    
    // Same company access
    if (userCompanyId === targetCompanyId) {
      return of(true);
    }
    
    // RC accessing supervised MC
    if (userRole === UserRole.OWNER) {
      return this.canRCAccessMC(userRole, userCompanyId, targetCompanyId);
    }
    
    return of(false);
  }

  // NEW: Enhanced role checks with company context
  canManageTemplatesInCompany(userRole: UserRole, userCompanyId: string, targetCompanyId: string): Observable<boolean> {
    if (userRole === UserRole.PLATFORM_OWNER) {
      return of(true);
    }
    
    return this.canAccessCompanyData(userRole, userCompanyId, targetCompanyId).pipe(
      map(canAccess => canAccess && (userRole === UserRole.OWNER || userRole === UserRole.ADMIN))
    );
  }

  // NEW: Check if user can access RC dashboard
  canAccessRCDashboard(role?: UserRole): boolean {
    return role === UserRole.OWNER;
  }

  // NEW: Check if user can access God dashboard
  canAccessGodDashboard(role?: UserRole): boolean {
    return role === UserRole.PLATFORM_OWNER;
  }

  // NEW: Check if user can manage RC-MC relationships
  canManageRCMCRelationships(role?: UserRole): boolean {
    return role === UserRole.PLATFORM_OWNER;
  }

  // NEW: Check if user can view isolation reports
  canViewIsolationReports(role?: UserRole): boolean {
    return role === UserRole.PLATFORM_OWNER || role === UserRole.OWNER;
  }

  // NEW: Get company type display
  getCompanyTypeDisplay(companyType?: 'RC' | 'MC'): string {
    switch (companyType) {
      case 'RC':
        return 'Recruitment Company';
      case 'MC':
        return 'Marketing Company';
      default:
        return 'Unknown';
    }
  }

  // NEW: Get company type badge class
  getCompanyTypeBadgeClass(companyType?: 'RC' | 'MC'): string {
    switch (companyType) {
      case 'RC':
        return 'badge-rc';
      case 'MC':
        return 'badge-mc';
      default:
        return 'badge-default';
    }
  }
}